import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AuthShell } from '@/components/layouts/auth-shell'
import { GoogleSignInButton } from '@/components/features/auth/google-sign-in-button'
import { EmailVerificationSteps, type VerificationStep } from '@/components/features/auth/email-verification-steps'
import { useForm, Link, usePage } from '@inertiajs/react'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { t } from "@/lib/i18n"
import { browserTimeZone } from "@/lib/time-zone"

interface RegistrationProps {
  upgradeMode?: boolean
  step: VerificationStep
  emailAddress: string | null
  codeLength: number
  codeTtlMinutes: number
  errors?: Record<string, string | string[] | undefined>
  // `usePage` constrains its type parameter to Inertia's PageProps, which
  // carries an index signature for the shared props the layout injects.
  [key: string]: unknown
}

/**
 * Google leads, and the email form waits behind a tap.
 *
 * Not a style preference. A Google sign-in arrives with the address already
 * verified by the provider, so it skips the code round-trip entirely, and it
 * never mints a password somebody later needs help recovering. Every diner who
 * takes that route is a diner we owe less machinery.
 *
 * The email form stays — not everyone has a Google account, or wants to hand
 * one over — but it is the second answer rather than the first thing on screen.
 *
 * The email route runs in three steps, and the order is the point: address,
 * code, then name and password. No `User` row exists until the code comes back,
 * so an address nobody holds never becomes an account. The address is not even
 * a field on the third step — the server reads it off the proof, so it cannot
 * be edited between proving one inbox and registering another.
 *
 * This screen is also where a seated guest upgrades, so neither route may cost
 * them their meal. See `Auth::GoogleIdentityService` and
 * `User::GuestMergeService`.
 */
export default function UserRegistrationsNew() {
  const {
    upgradeMode = false,
    step,
    emailAddress,
    codeLength,
    codeTtlMinutes,
    errors: pageErrors = {},
  } = usePage<RegistrationProps>().props

  const { data, setData, post, errors, processing } = useForm({
    name: '',
    password: '',
    password_confirmation: '',
    time_zone: browserTimeZone(),
  })

  // A rejected submit comes back as props, and collapsing the form on the way
  // would hide the very errors the diner has to read. Past the first step the
  // diner has already committed to the email route, so it stays open.
  const [showEmailForm, setShowEmailForm] = useState(
    () => step !== 'email' || Object.keys(pageErrors).length > 0
  )
  const revealRef = useRef<HTMLDivElement>(null)
  const revealedByTap = useRef(false)

  // The button that had focus unmounts the moment the form opens, which drops
  // focus to the body and makes a keyboard or screen-reader user tab from the
  // top of the page to reach the field they just asked for. Move focus to it.
  //
  // Only when they tapped: a form that opened because the submit was rejected
  // must leave focus alone rather than yanking it away from the error the diner
  // is reading.
  useEffect(() => {
    if (!showEmailForm || !revealedByTap.current) return
    revealedByTap.current = false
    revealRef.current?.querySelector<HTMLInputElement>('input')?.focus()
  }, [showEmailForm])

  const revealEmailForm = () => {
    revealedByTap.current = true
    setShowEmailForm(true)
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post('/registration')
  }

  const fieldError = (value: string | string[] | undefined): string | null => {
    if (!value) return null
    return Array.isArray(value) ? value[0] : value
  }

  return (
    <div data-testid="user-registration-view">
      <AuthShell title={t("ui.auth.titles.user_sign_up")} headline={t("ui.auth.user.headline")} blurb={t("ui.auth.user.blurb")}>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">
          {upgradeMode ? t("ui.user.registrations.complete_registration") : t("ui.user.registrations.create_account")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {upgradeMode
            ? t("ui.user.registrations.upgrade_description")
            : t("ui.user.registrations.create_description")}
        </p>
      </div>

      <div className="grid gap-6">
        {/* Only offered before the code round-trip starts. Once a code is
            in someone's inbox, a second sign-in route on the same screen
            is a fork in a flow they are already halfway through. */}
        {step === 'email' && (
          <>
            <GoogleSignInButton label={t("ui.react.pages.user.registrations.new.continue_with_google")} />
            <OrDivider label={t("ui.react.pages.user.registrations.new.or")} />
          </>
        )}

        {showEmailForm ? (
          <div ref={revealRef} className="grid gap-6">
            <EmailVerificationSteps
              step={step}
              emailAddress={emailAddress}
              codeLength={codeLength}
              codeTtlMinutes={codeTtlMinutes}
              errors={pageErrors}
              sendCodePath="/registration/send_code"
              verifyCodePath="/registration/verify_code"
              testIdPrefix="user-registration"
            />

            {step === 'details' && (
              <form id="register-email-form" onSubmit={handleSubmit} data-testid="register-email-form">
                <div className="grid gap-6">
                  <div className="grid gap-2">
                    <Label htmlFor="name">{t("ui.react.pages.user.registrations.new.name")}</Label>
                    <Input id="name" type="text" placeholder={t("ui.react.pages.user.registrations.new.john_doe")} value={data.name} onChange={(e) => setData('name', e.target.value)} required />
                    {fieldError(errors.name) && (<p className="text-sm font-medium text-destructive">{fieldError(errors.name)}</p>)}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="password">{t("ui.react.pages.user.registrations.new.password")}</Label>
                    <Input id="password" type="password" value={data.password} onChange={(e) => setData('password', e.target.value)} required />
                    {fieldError(errors.password) && (<p className="text-sm font-medium text-destructive">{fieldError(errors.password)}</p>)}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="password_confirmation">{t("ui.react.pages.user.registrations.new.confirm_password")}</Label>
                    <Input id="password_confirmation" type="password" value={data.password_confirmation} onChange={(e) => setData('password_confirmation', e.target.value)} required />
                    {fieldError(errors.password_confirmation) && (<p className="text-sm font-medium text-destructive">{fieldError(errors.password_confirmation)}</p>)}
                  </div>
                  <Button type="submit" className="w-full" disabled={processing} data-testid="register-submit-button">
                    {upgradeMode ? t("ui.user.registrations.submit_complete") : t("ui.user.registrations.submit_create")}
                  </Button>
                </div>
              </form>
            )}
          </div>
        ) : (
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            data-testid="register-with-email-button"
            aria-expanded={showEmailForm}
            aria-controls="register-email-form"
            onClick={revealEmailForm}
          >
            {t("ui.react.pages.user.registrations.new.register_with_email")}
          </Button>
        )}

        <div className="text-center text-sm">
          {upgradeMode ? (
            <Link href="/dashboard" className="underline underline-offset-4">{t("ui.react.pages.user.registrations.new.back_dashboard")}</Link>
          ) : (
            <>
              {t("ui.react.pages.user.registrations.new.already_tienes_bill")}{' '}
              <Link href="/session/new" className="underline underline-offset-4">{t("ui.react.pages.user.registrations.new.start_session")}</Link>
            </>
          )}
        </div>
      </div>
    </AuthShell>
    </div>
  )
}

function OrDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="h-px flex-1 bg-border" aria-hidden="true" />
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="h-px flex-1 bg-border" aria-hidden="true" />
    </div>
  )
}
